/**
 * Where a tapped push goes. Pure: no router, no native, no React.
 *
 * The server sends `{ screen, ...payload }` as FCM `data`
 * (`packages/modules/notifications/src/core/usecase/scheduled/scheduled-push.ts`),
 * and FCM `data` values are STRINGS ONLY, so `sessionId` arrives as text and
 * is validated here before it reaches the URL.
 *
 * | `screen`          | payload     | route                                  |
 * |-------------------|-------------|----------------------------------------|
 * | `today`           | –           | `?screen=today`                        |
 * | `soreness`        | `sessionId` | `?screen=today` (the check-in card surfaces from `GET /me/pending`) |
 * | `session-summary` | `sessionId` | `?sheet=summary&session=<sessionId>`   |
 * | anything else     | –           | `?screen=today`                        |
 *
 * An unknown `screen`, a missing or malformed payload, or no `data` at all
 * never throws: a newer server may send a screen this bundle does not know,
 * and a push must never blank the app. Each of those lands on Today.
 *
 * Every route CLEARS the layers the app had open (overlay, sheet and their
 * params), so the tap shows what it names rather than something under an open
 * coach. It is pushed, not replaced, so Back returns to where the app was.
 * The summary sheet keeps the base screen the app was on underneath it.
 */
import { CONVERSATION_ID_PATTERN } from "@repo/contract/iam";
import { z } from "zod";
import type { Search } from "../../app/search";

/** What the tap should refresh once it has navigated. */
export type PushRefresh = "pending" | "sessions";

export interface PushRoute {
  readonly refresh: readonly PushRefresh[];
  /** Search params to PUSH (merged over the current search). */
  readonly search: Partial<Search>;
}

/** The same shape the router accepts for `session` (`app/search.ts`). */
const SESSION_ID = /^[\w.:-]{1,64}$/;

const pushData = z.object({
  /** `D192`: the conversation a trainer reply belongs to — the server's own
   *  id pattern, so a malformed value is dropped rather than routed. */
  conversation: z
    .string()
    .max(64)
    .regex(CONVERSATION_ID_PATTERN)
    .optional()
    .catch(undefined),
  screen: z.string().optional().catch(undefined),
  sessionId: z.string().regex(SESSION_ID).optional().catch(undefined),
});

/**
 * Every layer param set to `undefined`, so the merge drops them and the
 * layer order (`nextOrder`) forgets the overlay and the sheet.
 */
const CLEAR_LAYERS: Partial<Search> = {
  about: undefined,
  ask: undefined,
  card: undefined,
  conversation: undefined,
  day: undefined,
  draft: undefined,
  edit: undefined,
  ex: undefined,
  exclude: undefined,
  item: undefined,
  muscle: undefined,
  never: undefined,
  overlay: undefined,
  pick: undefined,
  protocol: undefined,
  q: undefined,
  section: undefined,
  session: undefined,
  sheet: undefined,
  start: undefined,
  step: undefined,
};

const TODAY: Partial<Search> = { ...CLEAR_LAYERS, screen: "today" };

function parse(data: unknown): z.output<typeof pushData> {
  const parsed = pushData.safeParse(data);
  return parsed.success ? parsed.data : {};
}

/** The search params a tapped push navigates to. Never throws. */
export function routeForPush(data: unknown): Partial<Search> {
  const { conversation, screen, sessionId } = parse(data);
  if (screen === "trainer" && conversation !== undefined) {
    // The answer the trainer finished while nobody watched (`D192`): its
    // conversation, over Today — the same place every other door to the
    // trainer opens it.
    return {
      ...TODAY,
      conversation,
      overlay: "coach",
    };
  }
  if (screen === "session-summary" && sessionId !== undefined) {
    return {
      ...CLEAR_LAYERS,
      session: sessionId,
      sheet: "summary",
    };
  }
  return { ...TODAY };
}

/**
 * The server state a push announces, as query families to invalidate:
 * - `soreness`: the check-in is a `GET /me/pending` signal;
 * - `session-summary`: a session just ended, so the history list and the
 *   pending signals (the check-in is scheduled with it) moved;
 * - `today`, unknown, or no data: nothing.
 */
export function refreshForPush(data: unknown): readonly PushRefresh[] {
  const { screen } = parse(data);
  if (screen === "soreness") {
    return ["pending"];
  }
  if (screen === "session-summary") {
    return ["pending", "sessions"];
  }
  if (screen === "trainer") {
    // The unread dot, which a reply nobody has read yet just lit.
    return ["pending"];
  }
  return [];
}

/**
 * Whether a push that arrives with the app in FRONT is shown as a toast
 * (`D227`). A trainer reply is not: the server pushes when no socket watched
 * the answer arrive, and closing the trainer panel closes the socket without
 * leaving the app — so "Seu personal respondeu" landed on top of the screen
 * the person had just come back to. The unread dot it refreshes says it.
 */
export function toastsInForeground(data: unknown): boolean {
  return parse(data).screen !== "trainer";
}

/** Both halves, for the tap handler. */
export function pushRoute(data: unknown): PushRoute {
  return { refresh: refreshForPush(data), search: routeForPush(data) };
}
